import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Check, X, Loader2, Gift } from "lucide-react";
import { PlansAdmin } from "@/components/admin/PlansAdmin";
import { PromotionsAdmin } from "@/components/admin/PromotionsAdmin";
import { SubscriptionsOverview, CompanySubscriptionActions } from "@/components/admin/SubscriptionsOverview";
import { AuditLog } from "@/components/admin/AuditLog";
import { Pill } from "@/components/admin/shared";
import { brl, fmtDateTime, PAYMENT_STATES } from "@/lib/money";

const PAY_TONE = { pendente: "slate", aguardando_confirmacao: "amber", confirmado: "green", rejeitado: "red", expirado: "slate", cancelado: "slate" };

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [caps, setCaps] = useState(null);
  const [busy, setBusy] = useState("");
  const [tab, setTab] = useState("visao");

  const load = async () => {
    try {
      const [p, c, pl, pr, cp] = await Promise.all([api.get("/admin/payments"), api.get("/admin/companies"), api.get("/admin/plans"), api.get("/admin/promotions"), api.get("/admin/billing/capabilities")]);
      setPayments(p.data); setCompanies(c.data); setPlans(pl.data); setPromotions(pr.data); setCaps(cp.data);
    } catch { toast.error("Acesso restrito."); navigate("/dashboard"); }
  };
  useEffect(() => { if (user && !user.is_superadmin) navigate("/dashboard"); else load(); }, [user]); // eslint-disable-line

  const act = async (label, fn) => { setBusy(label); try { await fn(); toast.success("Feito!"); await load(); } catch (e) { toast.error(e.response?.data?.detail || "Erro."); } finally { setBusy(""); } };
  const waiting = payments.filter((p) => p.status === "aguardando_confirmacao").length;

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="admin-page">
      <div>
        <p className="mb-1 inline-flex items-center gap-2 text-xs font-mono-flow uppercase tracking-widest text-cyan-400"><Sparkles className="h-3.5 w-3.5" /> Painel FlowIA</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Admin · Assinaturas</h1>
        <p className="mt-1 text-sm text-slate-400">Somente administradores. Todas as ações aqui ficam registradas na auditoria.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex h-auto flex-wrap justify-start bg-slate-900/70">
          <TabsTrigger value="visao" data-testid="admin-tab-overview">Visão geral</TabsTrigger>
          <TabsTrigger value="pagamentos" data-testid="admin-tab-payments">Pagamentos{waiting > 0 && <span className="ml-1.5 rounded-full bg-amber-400 px-1.5 text-[10px] font-bold text-slate-950">{waiting}</span>}</TabsTrigger>
          <TabsTrigger value="empresas" data-testid="admin-tab-companies">Empresas</TabsTrigger>
          <TabsTrigger value="planos" data-testid="admin-tab-plans">Planos e preços</TabsTrigger>
          <TabsTrigger value="promocoes" data-testid="admin-tab-promotions">Promoções</TabsTrigger>
          <TabsTrigger value="auditoria" data-testid="admin-tab-audit">Auditoria</TabsTrigger>
        </TabsList>

        <TabsContent value="visao" className="mt-4"><SubscriptionsOverview plans={plans} promotions={promotions} companies={companies} /></TabsContent>

        <TabsContent value="pagamentos" className="glow-card mt-4 overflow-hidden rounded-2xl">
          {payments.length === 0 && <p className="p-8 text-center text-sm text-slate-500">Nenhum pagamento ainda.</p>}
          {payments.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800/50 px-5 py-4" data-testid="admin-payment-row">
              <div>
                <p className="font-semibold text-slate-100">{p.company_name || p.company_id} <span className="ml-2 text-sm text-slate-400">{brl(p.amount)} · {p.plan_name || p.plan_id || "Starter"} {p.period === "annual" ? "anual" : "mensal"}</span></p>
                <p className="text-xs text-slate-500">{p.provider === "infinitepay" ? "InfinitePay" : `Pix manual · txid ${p.txid}`} · criado {fmtDateTime(p.created_at)}{p.paid_reported_at ? ` · informado ${fmtDateTime(p.paid_reported_at)}` : ""}{p.coupon_code ? ` · cupom ${p.coupon_code}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <Pill tone={PAY_TONE[p.status] || "slate"} testid="admin-payment-status">{PAYMENT_STATES[p.status] || p.status}</Pill>
                {p.provider !== "infinitepay" && (p.status === "pendente" || p.status === "aguardando_confirmacao") && (
                  <>
                    <Button size="sm" disabled={!!busy} onClick={() => act(p.id, () => api.post(`/admin/payments/${p.id}/approve`))} className="bg-emerald-500 font-semibold text-slate-950 hover:bg-emerald-400" data-testid="admin-approve-btn">{busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="mr-1 h-4 w-4" /> Confirmar Pix</>}</Button>
                    <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act(p.id + "r", () => api.post(`/admin/payments/${p.id}/reject`))} className="border-slate-700 bg-slate-900/50 text-slate-300 hover:text-red-300" data-testid="admin-reject-btn"><X className="h-4 w-4" /></Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="empresas" className="glow-card mt-4 overflow-hidden rounded-2xl">
          {companies.map((c) => (
            <div key={c.company_id} className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800/50 px-5 py-4" data-testid="admin-company-row">
              <div>
                <p className="font-semibold text-slate-100">{c.name} <span className="text-xs text-slate-500">{c.segment}</span></p>
                <p className="text-xs text-slate-500">{c.owner?.email} · WhatsApp {c.whatsapp_status} · plano {c.plan_name || c.plan} · {c.plan_status === "active" ? `válido até ${fmtDateTime(c.plan_expires_at).slice(0, 8)}` : c.trial_ends_at ? `teste até ${fmtDateTime(c.trial_ends_at).slice(0, 8)}` : ""}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <CompanySubscriptionActions company={c} onDone={load} />
                <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act(c.company_id, () => api.post(`/admin/companies/${c.company_id}/activate?months=1`))} className="border-slate-700 bg-slate-900/50 text-cyan-300" data-testid="admin-activate-btn" title="Liberar 1 mês manualmente"><Gift className="mr-1 h-4 w-4" /> +1 mês</Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="planos" className="mt-4"><PlansAdmin capabilities={caps} /></TabsContent>
        <TabsContent value="promocoes" className="mt-4"><PromotionsAdmin /></TabsContent>
        <TabsContent value="auditoria" className="mt-4"><AuditLog /></TabsContent>
      </Tabs>
    </div>
  );
}
